import { Component, ElementRef, OnInit, signal, ViewChild } from '@angular/core';
import { DocumentFileTypes, FeatureFlag, ImageFileTypes } from '@app/enums';
import { SelfOnboardingDocumentUploader } from '@app/modules/payroll/interfaces/self-onboarding/self-onboarding-document-uploader.interface';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import {
    PAYROLL_SUPPORTING_DOCUMENTS,
    SupportingDocumentType,
} from '@app/modules/payroll/types/self-onboarding-document-type.type';
import { OnboardingStatusService } from '@app/modules/self-serve/services/onboarding-status.service';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { SupportingDocument } from '@models/payroll/self-onboarding-document.model';
import { SelfOnboardingDocumentsService } from '@payroll/services/self-onboarding-documents.service';

@Component({
    selector: 'app-self-onboarding-supporting-documents',
    templateUrl: './supporting-documents.template.html',
    styleUrl: './supporting-documents.style.scss',
})
export class SelfOnboardingSupportingDocumentsView implements OnInit {
    isLoading = true;
    documentTypes = PAYROLL_SUPPORTING_DOCUMENTS;
    documents?: Record<SupportingDocumentType, SupportingDocument>;
    acceptableFileTypes = [
        DocumentFileTypes.pdf,
        DocumentFileTypes.doc,
        DocumentFileTypes.docx,
        ImageFileTypes.jpeg,
        ImageFileTypes.jpg,
        ImageFileTypes.png,
    ];

    isKYCKYBFlagEnabled = signal(false);

    @ViewChild('fileInput') private fileInput!: ElementRef<HTMLInputElement>;

    private currentFileType?: SupportingDocumentType;

    constructor(
        private documentsService: SelfOnboardingDocumentsService,
        private notify: NotifyService,
        private auth: AuthService,
        private onboardingStatusService: OnboardingStatusService,
        private featureService: FeatureService,
        public payrollModuleMenuService: PayrollModuleMenuService
    ) {
        this.featureService.has(FeatureFlag.KYC_KYB).then(this.isKYCKYBFlagEnabled.set);
    }

    async ngOnInit(): Promise<void> {
        this.documents = await this.documentsService.getDocuments(PAYROLL_SUPPORTING_DOCUMENTS);

        this.isLoading = false;
    }

    /**
     * Checks if there are any legacy support documents uploaded
     * When the KYB flag is enabled, we only show these documents if the user has already uploaded them
     */
    get hasUploadedDocuments(): boolean {
        if (!this.documents) {
            return false;
        }
        return Object.values(this.documents).some((doc) => !!doc.createdAt);
    }

    trackByType(_: number, upload: SupportingDocumentType): string {
        return upload;
    }

    getFile(fileType: SupportingDocumentType): void {
        this.currentFileType = fileType;
        this.fileInput.nativeElement.click();
    }

    async uploadDocument(target: EventTarget | null): Promise<void> {
        const { files } = (target as HTMLInputElement) ?? {};

        if (!files) {
            return;
        }

        if (files.length === 0) {
            return;
        }

        if (this.currentFileType === undefined) {
            return;
        }

        if (this.documents === undefined) {
            return;
        }

        const file = files[0];

        this.isLoading = true;

        const uploaderInfo: SelfOnboardingDocumentUploader = {
            documentUploaderEmailAddress: this.auth.employee?.account?.email ?? '',
            documentUploaderFirstName: this.auth.employee?.firstName ?? '',
            documentUploaderLastName: this.auth.employee?.lastName ?? '',
        };

        try {
            const document = await this.documentsService.uploadFile(this.currentFileType, file, uploaderInfo);
            this.documents[this.currentFileType] = document;
            this.notify.success('payroll.setup.yourUploadWasSuccessful');
        } catch (error) {
            this.notify.error(error);
        }

        this.isLoading = false;
        this.onboardingStatusService.refresh();

        (this.fileInput.nativeElement.value as string | null) = null;
        delete this.currentFileType;
    }
}
